import { ApiError } from '@app/core/models';
import {
	RecipientAddress,
	Parcel,
	ParcelApiOnlyProps,
	ParcelDimensions,
	ParcelFromAPI,
	RecipientDeliveryPoint,
	ParcelToAPI,
	SelectedExtraServices,
	LastMileType,
	Recipient,
	type FinalDeliveryAddress,
} from '@app/ecsomag/models';
import { ExtraService, ExtraServicesAdditionalDataMap, ExtraServicesPartialAdditionalDataMap } from '@app/models/prices';
import { NullableProps } from '../../../../core/utils/types';
import { pick } from 'lodash-es';

// const TEST_ORDER_ID = '28c8c6de-e1bb-4e8c-8c90-9c160a2a0245';

export type ParcelEntity = {
	__id: string;
	id?: string;
	dimensions: Partial<NullableProps<ParcelDimensions>>;
	destination: {
		isoCode: string;
		type: LastMileType;
		deliveryAddress: Partial<Recipient> & {
			address: Partial<RecipientAddress>;
			parcelLocker: Partial<RecipientDeliveryPoint>;
			parcelShop: Partial<RecipientDeliveryPoint>;
		};
		finalDeliveryAddress?: Partial<FinalDeliveryAddress>;
	};
	extraServices: {
		id: ExtraService['id'];
		selected: boolean;
		data?: Partial<ExtraServicesPartialAdditionalDataMap>;
	}[];

	// parcelNumber?: string;
	// paymentAmount?: number;
	// size?: PackageSizeName;
	// pickupDate?: string;
	// pickupAddress?: PickupAddress;
	latestApiErrors?: {
		timestamp: number;
		errors: ApiError[];
	};
};

export const generateTemporaryParcelId = () => `temp-id:${Math.random().toString(36).substring(2)}`;

export const createNewParcelEntity = (): ParcelEntity => ({
	__id: generateTemporaryParcelId(),
	dimensions: {},
	destination: {
		isoCode: 'HU',
		type: 'address',
		deliveryAddress: {
			address: {},
			parcelLocker: {},
			parcelShop: {},
		},
	},
	extraServices: [],
});

export const mapParcelDestinationToEntityDestination = (destination: Parcel['destination']): ParcelEntity['destination'] => {
	return {
		isoCode: destination.isoCode,
		type: destination.type,
		deliveryAddress: {
			address: destination.type === 'address' ? destination.deliveryAddress : {},
			parcelLocker: destination.type === 'parcel-locker' ? destination.deliveryAddress : {},
			parcelShop: destination.type === 'parcel-shop' ? destination.deliveryAddress : {},
		},
		finalDeliveryAddress: destination.type !== 'address' ? destination.finalDeliveryAddress : undefined,
	};
};

export const mapEntityDestinationToParcelDestination = (destination: ParcelEntity['destination']): Parcel['destination'] => {
	switch (destination.type) {
		case 'address': {
			return {
				type: destination.type,
				isoCode: destination.isoCode,
				deliveryAddress: destination.deliveryAddress?.address as RecipientAddress,
			};
		}
		case 'parcel-shop':
		case 'parcel-locker': {
			return {
				type: destination.type,
				isoCode: destination.isoCode,
				deliveryAddress: destination.deliveryAddress?.parcelShop as RecipientDeliveryPoint,
				finalDeliveryAddress: destination.finalDeliveryAddress as FinalDeliveryAddress,
			};
		}
	}
};

export const mapParcelExtraServicesToEntityExtraServices = (extraServices: ExtraService[], selectedExtraServices: SelectedExtraServices): ParcelEntity['extraServices'] => {
	return extraServices.map(extraService => {
		const selectedExtraService = selectedExtraServices.find(selectedExtraService => selectedExtraService.id === extraService.id);
		return {
			id: extraService.id,
			selected: !!selectedExtraService,
			data: selectedExtraService?.data ?? {},
		};
	});
};

export const mapEntityExtraServicesToParcelExtraServices = (extraServices: ParcelEntity['extraServices']): SelectedExtraServices => {
	return extraServices
		.filter(extraService => extraService.selected)
		.map(extraService => {
			return {
				id: extraService.id,
				data: extraService.data as Partial<ExtraServicesAdditionalDataMap>,
			};
		});
};

// export const mapEntityToParcel = (parcel: ParcelEntity): Parcel => {
// 	return {
// 		id: parcel.id,
// 		dimensions: parcel.dimensions,

// 	}
// }

export const mapEntityToApi = (parcel: ParcelEntity): ParcelToAPI => {
	const destination = mapEntityDestinationToParcelDestination(parcel.destination);
	const extraServices = mapEntityExtraServicesToParcelExtraServices(parcel.extraServices);
	const recipient = pick(parcel.destination.deliveryAddress, 'name', 'mobile', 'email') as Recipient;
	let deliveryAddress: ParcelToAPI['deliveryAddress'];
	switch (destination.type) {
		case 'address': {
			deliveryAddress = {
				...recipient,
				...(parcel.destination.deliveryAddress?.address as RecipientAddress),
				type: 'address',
				_type: 'address',
				isoCode: parcel.destination.isoCode,
			};
			break;
		}
		case 'parcel-locker': {
			deliveryAddress = {
				...recipient,
				...(parcel.destination.deliveryAddress?.parcelLocker as RecipientDeliveryPoint),
				type: 'parcel-locker',
				_type: 'parcel-locker',
				isoCode: parcel.destination.isoCode,
			};
			break;
		}
		case 'parcel-shop': {
			deliveryAddress = {
				...recipient,
				...(parcel.destination.deliveryAddress?.parcelShop as RecipientDeliveryPoint),
				type: 'parcel-shop',
				_type: 'parcel-shop',
				isoCode: parcel.destination.isoCode,
			};
			break;
		}
	}
	return {
		id: parcel.id,
		isoCode: destination.isoCode,
		length: parcel.dimensions.length ?? 0,
		width: parcel.dimensions.width ?? 0,
		height: parcel.dimensions.height ?? 0,
		extraServices,
		deliveryAddress,
		finalDeliveryAddress: destination.type !== 'address' ? destination.finalDeliveryAddress : undefined,
		lastMileType: deliveryAddress._type,
	};
};

export const mapApiToEntity = (extraServices: ExtraService[], parcel: ParcelFromAPI): ParcelEntity & ParcelApiOnlyProps => {
	const destinationType = parcel.deliveryAddress.type;
	const recipient = pick(parcel.deliveryAddress, 'name', 'mobile', 'email') as Recipient;

	let destination: ParcelEntity['destination'];
	switch (destinationType) {
		case 'address': {
			destination = {
				type: 'address',
				isoCode: parcel.deliveryAddress.isoCode,
				deliveryAddress: {
					...recipient,
					address: {
						type: 'address',
						zipCode: parcel.deliveryAddress.zipCode,
						city: parcel.deliveryAddress.city,
						street: parcel.deliveryAddress.street,
						houseNumber: parcel.deliveryAddress.houseNumber,
						houseNumberInfo: parcel.deliveryAddress.houseNumberInfo,
					},
					parcelLocker: {},
					parcelShop: {},
				},
			};
			break;
		}
		case 'parcel-locker': {
			destination = {
				type: 'parcel-locker',
				isoCode: parcel.deliveryAddress.isoCode,
				deliveryAddress: {
					...recipient,
					address: {},
					parcelLocker: {
						parcelShopId: parcel.deliveryAddress.parcelShopId,
					},
					parcelShop: {},
				},
			};
			break;
		}
		case 'parcel-shop': {
			destination = {
				type: 'parcel-shop',
				isoCode: parcel.deliveryAddress.isoCode,
				deliveryAddress: {
					...recipient,
					address: {},
					parcelLocker: {},
					parcelShop: {
						parcelShopId: parcel.deliveryAddress.parcelShopId,
					},
				},
			};
			break;
		}
	}

	// console.log(parcel.deliveryAddress, destination);
	return {
		__id: parcel.id,
		id: parcel.id,
		dimensions: {
			length: parcel.length,
			width: parcel.width,
			height: parcel.height,
		},
		destination, // : mapParcelDestinationToEntityDestination(parcelDestination),
		extraServices: mapParcelExtraServicesToEntityExtraServices(extraServices, parcel.extraServices),

		parcelNumber: parcel.parcelNumber,
		totalPrice: parcel.totalPrice,
		size: parcel.size,
		pickupDate: parcel.pickupDate,
		pickupAddress: parcel.pickupAddress,

		delivered: parcel.delivered,
		deliveredAt: parcel.deliveredAt,
		deliveryStatus: parcel.deliveryStatus,
	};
};

// type guards
export type ParcelEntityPropsDimensions = Pick<ParcelEntity, 'dimensions'>;
export type ParcelEntityPropsDestination = Pick<ParcelEntity, 'destination'>;
export type ParcelEntityDestinationPropsType = Omit<ParcelEntity['destination'], 'type'> & Pick<ParcelEntity['destination'], 'type'>;
export type ParcelEntityDestinationPropsIsoCode = Omit<ParcelEntity['destination'], 'isoCode'> & Pick<ParcelEntity['destination'], 'isoCode'>;
export type ParcelEntityDestinationPropsDeliveryAddress = Omit<ParcelEntity['destination'], 'deliveryAddress'> & Pick<ParcelEntity['destination'], 'deliveryAddress'>;
export type ParcelEntityPropsDestinationAll = { destination: ParcelEntity['destination'] };
export type ParcelEntityPropsExtraServices = Pick<ParcelEntity, 'extraServices'>;

export interface ParcelEntityWithDimensions extends ParcelEntity {
	dimensions: Parcel['dimensions'];
}
export interface ParcelEntityWithDestination extends ParcelEntity {
	destination: ParcelEntity['destination'];
}
export interface ParcelEntityWithDestinationCountryAndType extends ParcelEntity {
	destination: Pick<ParcelEntity['destination'], 'isoCode' | 'type' | 'deliveryAddress'>;
}

export function isParcelDimensions(dimensions: ParcelEntity['dimensions']): dimensions is ParcelDimensions {
	return !!dimensions?.width && !!dimensions?.height && !!dimensions?.length;
}

export function parcelHasDimensions(parcel: ParcelEntity): parcel is ParcelEntityWithDimensions {
	return !!parcel.dimensions?.width && !!parcel.dimensions?.height && !!parcel.dimensions?.length;
}

export function parcelDestinationHasIsoCode(parcelDestination: ParcelEntity['destination']): parcelDestination is ParcelEntityDestinationPropsIsoCode {
	return !!parcelDestination.isoCode;
}
export function parcelDestinationHasType(parcelDestination: ParcelEntity['destination']): parcelDestination is ParcelEntityDestinationPropsType {
	return !!parcelDestination.type;
}
export function parcelDestinationHasDeliveryAddress(parcelDestination: ParcelEntity['destination']): parcelDestination is ParcelEntityDestinationPropsDeliveryAddress {
	return !!parcelDestination.deliveryAddress; // ? isDeliveryAddress(parcelDestination.deliveryAddress) : false;
}

export function parcelHasDestinationCountryAndType(parcel: ParcelEntity): parcel is ParcelEntityWithDestinationCountryAndType {
	return parcelDestinationHasIsoCode(parcel.destination) && parcelDestinationHasType(parcel.destination);
}
export function parcelHasDestination(parcel: ParcelEntity): parcel is ParcelEntityWithDestination {
	return parcelHasDestinationCountryAndType(parcel) && parcelDestinationHasDeliveryAddress(parcel.destination);
}

export function parcelExtraServicesIsValid(parcel: ParcelEntity): parcel is Omit<ParcelEntity, 'extraServices'> & ParcelEntityPropsExtraServices {
	return (
		(!!parcel.extraServices &&
			parcel.extraServices
				.filter(extraService => extraService.id === 4)
				.every(
					extraService => extraService.data?.['cod']?.amount // && extraService.data?.['cod']?.bankAccountHolder && extraService.data?.['cod']?.bankAccountNumber
				)) ||
		false
	);
}

export function parcelHasDestinationCountryAndSize(
	parcel: ParcelEntity
): parcel is Omit<ParcelEntity, 'destination' | 'dimensions'> & ParcelEntityWithDestinationCountryAndType & ParcelEntityWithDimensions {
	return parcelHasDestinationCountryAndType(parcel) && parcelHasDimensions(parcel);
}

export function parcelHasDestinationCountryAndSizeAndExtraServices(
	parcel: ParcelEntity
): parcel is Omit<ParcelEntity, 'destination' | 'dimensions' | 'extraServices'> & ParcelEntityWithDestinationCountryAndType & ParcelEntityWithDimensions & ParcelEntityPropsExtraServices {
	return parcelHasDestinationCountryAndSize(parcel) && parcelExtraServicesIsValid(parcel);
}

export function parcelIsValid(parcel: ParcelEntity): parcel is ParcelEntity & ParcelEntityPropsDimensions & ParcelEntityPropsDestinationAll & ParcelEntityPropsExtraServices {
	return parcelHasDimensions(parcel) && parcelHasDestination(parcel) && parcelExtraServicesIsValid(parcel);
}
